import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet } from '../generic/Api_Functions'

// P4: a burst of drag/filter/publish events must collapse into a single call, and only the answer
// to the LAST one may ever paint.
const DEBOUNCE_MS = 400

// `apiGet` turns its params object into a querystring through `URLSearchParams`, which serialises
// `null`/`undefined` as the literal string "null"/"undefined" -- Sun would then read e.g.
// `clientId=null` instead of "no filter" (RN5: absent means "all"). Empty strings are dropped for
// the same reason.
const withoutEmptyValues = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '')
)

/**
 * Shared fetcher for both `planMetrics/{planificationId}/...` sub-resources: the indicators ribbon
 * (this cycle, story 153) and the load-capacity block (a later cycle), which is why `endpoint` and
 * `params` are both arguments instead of a hardcoded path.
 *
 * - A1/RN55: never calls while there is no selected plan, or while the caller disables it (missing
 *   permission, or -- for the future load-capacity block -- a collapsed accordion).
 * - P4: debounces `DEBOUNCE_MS` and discards any response that is no longer the latest in-flight
 *   request, so a burst of changes only ever paints once, with the last answer.
 * - On failure the stale `data` is cleared: CU7/S79 forbids painting old figures as if they were
 *   current.
 */
const usePlanMetrics = ({ planificationId, endpoint, params = {}, enabled = true }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)
  const latestRequestIdRef = useRef(0)
  const paramsRef = useRef(params)
  paramsRef.current = params

  const isActive = enabled && planificationId !== null && planificationId !== undefined
  const paramsKey = JSON.stringify(withoutEmptyValues(params))

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  useEffect(() => {
    if (!isActive) {
      setData(null)
      setError(null)
      setLoading(false)
      return undefined
    }

    const requestId = ++latestRequestIdRef.current
    setLoading(true)

    const timeoutId = setTimeout(() => {
      apiGet(`planMetrics/${planificationId}/${endpoint}`, withoutEmptyValues(paramsRef.current))
        .then((response) => {
          if (requestId !== latestRequestIdRef.current) {
            return
          }
          setData(response)
          setError(null)
          setLoading(false)
        })
        .catch((requestError) => {
          if (requestId !== latestRequestIdRef.current) {
            return
          }
          setData(null)
          setError(requestError)
          setLoading(false)
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timeoutId)
    }
    // `paramsKey` stands in for `params`, which is a new object identity on every render otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, planificationId, endpoint, paramsKey, reloadToken])

  return { data, loading, error, reload }
}

export default usePlanMetrics
